import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight, Users, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Match the labels shown on CirclePage so the language stays coherent.
const TYPE_LABEL = {
  question:   'Today\u2019s question',
  exercise:   'Today\u2019s exercise',
  discussion: 'Today\u2019s discussion',
};
const TYPE_EMOJI = { question: '❓', exercise: '🧘', discussion: '💬' };

/**
 * CircleDiscoveryCarousel — renders on Home for signed-in users. Shows every
 * circle the user hasn't joined yet, each card previewing today's rotating
 * question/exercise/discussion. Tapping any card opens `/circles/:id`.
 *
 * Signed-out users see the same carousel but with all 8 circles (nothing to
 * exclude yet) — it doubles as a "here's what's inside" affordance for the
 * anonymous audience.
 */
const CircleDiscoveryCarousel = () => {
  const { user } = useAuth();
  const [cards,  setCards]  = useState([]);
  const [joined, setJoined] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('rbr_token');
        const auth = t ? { Authorization: `Bearer ${t}` } : {};
        // Personalized when we send auth; anonymous otherwise.
        const [tRes, meRes] = await Promise.all([
          fetch(`${API_URL}/api/community/circles-today`, { headers: auth }),
          user ? fetch(`${API_URL}/api/community/me`, { headers: authHeaders() }) : Promise.resolve(null),
        ]);
        if (tRes.ok) setCards((await tRes.json()).circles || []);
        if (meRes && meRes.ok) setJoined((await meRes.json()).circles || []);
      } catch { /* silent — Home has lots of content, skip on failure */ }
      finally { setLoading(false); }
    })();
  }, [user, authHeaders]);

  const unjoined = cards.filter((c) => !joined.includes(c.circle.id));
  // Show unjoined first. If user has joined all 8, we hide the whole section.
  if (loading || unjoined.length === 0) return null;
  const personalized = unjoined.some((c) => c.recommended);

  return (
    <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }} data-testid="home-circle-discovery">
      <div className="px-5 flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: '#D6AD60' }}>
            {personalized ? 'Picked For You' : 'Find Your Circle'}
          </p>
          <p className="text-sm text-stone-500 mt-0.5" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            {personalized
              ? 'Based on how you\u2019ve been checking in lately.'
              : 'People walking the same road as you.'}
          </p>
        </div>
        <Link
          to="/community"
          data-testid="home-circle-discovery-all"
          className="text-[11px] font-semibold uppercase tracking-wide text-[#D6AD60] hover:text-[#AA8347] flex items-center gap-1 transition-colors"
        >
          All
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 pb-2 -mx-1">
        {unjoined.map((c) => {
          const accent = c.circle.accent || '#D6AD60';
          const label  = TYPE_LABEL[c.type] || 'Today';
          const emoji  = TYPE_EMOJI[c.type] || '';
          return (
            <Link
              key={c.circle.id}
              to={`/circles/${c.circle.id}`}
              className="flex-shrink-0 w-[240px] rounded-2xl p-4 transition-all active:scale-[0.98] hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              style={{
                background: '#FFFCF3',
                border: `1px solid ${accent}55`,
                borderTop: `3px solid ${accent}`,
                WebkitTapHighlightColor: 'transparent',
              }}
              data-testid={`home-circle-card-${c.circle.id}`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}bb)` }}
                >
                  {c.circle.emoji}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-semibold text-stone-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {c.circle.title}
                    </p>
                    {c.recommended && (
                      <span
                        title="Picked for you based on your check-ins"
                        className="inline-flex items-center gap-0.5 flex-shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                        style={{ background: '#E76F51', color: '#FFFCF3' }}
                        data-testid={`home-circle-recommended-${c.circle.id}`}
                      >
                        <Heart className="w-2.5 h-2.5" /> For You
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-stone-400 flex items-center gap-1">
                    <Users className="w-2.5 h-2.5" /> {c.members} {c.members === 1 ? 'member' : 'members'}
                  </p>
                </div>
              </div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1 flex items-center gap-1" style={{ color: accent }}>
                <Sparkles className="w-2.5 h-2.5" />
                {label} · {emoji}
              </p>
              <p className="text-[13px] leading-snug text-stone-700 line-clamp-2 min-h-[2.3rem]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {c.title || 'Something to sit with today.'}
              </p>
              <p className="text-[10px] text-[#AA8347] font-semibold uppercase tracking-wide mt-2 flex items-center gap-1">
                Open circle <ChevronRight className="w-3 h-3" />
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default CircleDiscoveryCarousel;
